import {
    API_BASE, ERROR_CODES,
    type ActionResult, type Agent, type AgentRole, type Answer, type AskInput, type AskReply, type Connection, type ConnectionTest,
    type DecisionInput, type ErrorCode, type FormStatus, type FormsPage, type FormsSummary, type Live, type Me, type Memory,
    type Overview, type PendingAction, type PendingDetail, type PendingItem,
} from '@/contract'
import type { DecisionLogEntry, LiveExtras, PendingExtras } from '@/contract'
import type { ChecklistItem, ConnectionDetail, ConnectionHealth } from '@/contract'
import type { AgentFlow } from '@/contract'
import type { AgentRequest, AgentRequestInput } from '@/contract'
import type { OrgChart } from '@/contract'
import type { AuthOptions, PasswordLoginInput } from '@/contract'
import type { Usage, UsagePeriod } from '@/contract'

/* Spec COG-005 — HTTP client of the panel API (/api/v1).
   Envelope { data } on success and { error: { code } } on failure; 204 becomes undefined. */

export type ApiErrorCode = ErrorCode | 'network' | 'unknown'

export class ApiError extends Error {
    constructor(
        readonly status: number,
        readonly code: ApiErrorCode,
        /** Frase curta que o servidor mandou junto do código (ex.: 409 do ERP), para mostrar como veio. */
        readonly detail: string | null = null,
    ) {
        super(code)
        this.name = 'ApiError'
    }
}

export function isSessionError(error: unknown): boolean {
    return error instanceof ApiError && error.code === 'session_required'
}

export function toApiError(status: number, body: unknown): ApiError {
    const error = body && typeof body === 'object' ? (body as { error?: unknown }).error : null
    if (error && typeof error === 'object') {
        const code = (error as { code?: unknown }).code
        if (typeof code === 'string' && (ERROR_CODES as readonly string[]).includes(code)) {
            const message = (error as { message?: unknown }).message
            return new ApiError(status, code as ErrorCode, typeof message === 'string' && message.length <= 200 ? message : null)
        }
    }
    if (status === 401) return new ApiError(status, 'session_required')
    if (status === 403) return new ApiError(status, 'forbidden')
    if (status === 404) return new ApiError(status, 'not_found')
    if (status === 502 || status === 503 || status === 504) return new ApiError(status, 'unavailable')
    return new ApiError(status, 'unknown')
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let res: Response
    try {
        res = await fetch(`${API_BASE}${path}`, {
            method,
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                // FormData (envio de arquivo): o navegador poe o Content-Type multipart com o boundary
                ...(body !== undefined && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            },
            body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
        })
    } catch {
        throw new ApiError(0, 'network')
    }
    if (res.status === 204) return undefined as T
    let json: unknown = null
    const text = await res.text()
    if (text) {
        try {
            json = JSON.parse(text)
        } catch {
            json = null
        }
    }
    if (!res.ok) throw toApiError(res.status, json)
    if (!json || typeof json !== 'object' || !('data' in json)) throw new ApiError(res.status, 'unknown')
    return (json as { data: T }).data
}

const enc = encodeURIComponent

/** Action -> route segment of POST /pending/:id/<segment>. */
export const ACTION_PATH: Record<PendingAction, string> = { approve: 'approve', reject: 'reject', return_to_agent: 'return', close: 'close' }

export type FormsFilter = { status?: FormStatus; q?: string; page?: number }

/** "?status=charged&q=ana&page=2" (empty values left out). */
export function formsSearch({ status, q, page }: FormsFilter): string {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    const term = q?.trim()
    if (term) params.set('q', term)
    if (page && page > 1) params.set('page', String(page))
    const text = params.toString()
    return text ? `?${text}` : ''
}

export const panelApi = {
    me: () => request<Me>('GET', '/me'),
    overview: () => request<Overview>('GET', '/overview'),
    agents: () => request<Agent[]>('GET', '/agents'),
    agentRole: (slug: string) => request<AgentRole>('GET', `/agents/${enc(slug)}/role`),
    connections: () => request<Connection[]>('GET', '/connections'),
    testConnection: (slug: string) => request<ConnectionTest>('POST', `/connections/${enc(slug)}/test`),
    logout: () => request<void>('POST', '/auth/logout'),
    authOptions: () => request<AuthOptions>('GET', '/auth/options'),
    passwordLogin: (input: PasswordLoginInput) => request<void>('POST', '/auth/password', input),
    pending: () => request<PendingItem[]>('GET', '/pending'),
    pendingDetail: (id: string) => request<PendingDetail>('GET', `/pending/${enc(id)}`),
    decide: (id: string, action: PendingAction, input: DecisionInput) =>
        request<ActionResult>('POST', `/pending/${enc(id)}/${ACTION_PATH[action]}`, input),
    pauseAgent: (slug: string) => request<Agent>('POST', `/agents/${enc(slug)}/pause`),
    resumeAgent: (slug: string) => request<Agent>('POST', `/agents/${enc(slug)}/resume`),
    forms: (filter: FormsFilter) => request<FormsPage>('GET', `/forms${formsSearch(filter)}`),
    formsSummary: () => request<FormsSummary>('GET', '/forms/summary'),
    live: () => request<Live>('GET', '/live'),
    memory: () => request<Memory>('GET', '/memory'),
    ask: (input: AskInput) => request<Answer>('POST', '/ask', input),
    askReply: (ticket: string) => request<AskReply>('GET', `/ask/${enc(ticket)}`),
    /* Phase 6: review and live extras */
    pendingExtras: (id: string) => request<PendingExtras>('GET', `/pending/${enc(id)}/extras`),
    decisions: (limit = 20) => request<DecisionLogEntry[]>('GET', `/decisions?limit=${limit}`),
    liveExtras: () => request<LiveExtras>('GET', '/live/extras'),
    /* Phase 6b: agent flows (Ao Vivo lanes and big map) */
    liveFlows: () => request<AgentFlow[]>('GET', '/live/flows'),
    liveFlow: (slug: string) => request<AgentFlow>('GET', `/live/flows/${enc(slug)}`),
    /* Phase 6: system health and "Para liberar" */
    connectionsHealth: () => request<ConnectionHealth[]>('GET', '/connections/health'),
    connectionDetail: (slug: string) => request<ConnectionDetail>('GET', `/connections/${enc(slug)}`),
    addChecklistStep: (slug: string, text: string) => request<ChecklistItem>('POST', `/connections/${enc(slug)}/checklist`, { text }),
    setChecklistStep: (slug: string, id: string, done: boolean) =>
        request<ChecklistItem>('POST', `/connections/${enc(slug)}/checklist/${enc(id)}`, { done }),
    /* Phase 7: "Pedir um novo agente" (the panel only records the request) */
    agentRequests: () => request<AgentRequest[]>('GET', '/agent-requests'),
    requestAgent: (input: AgentRequestInput) => request<AgentRequest>('POST', '/agent-requests', input),
    /* Phase 8: agents org chart */
    agentsOrg: () => request<OrgChart>('GET', '/agents/org'),
    /* Phase 8: AI usage (tokens and estimated cost) */
    usage: (period: UsagePeriod) => request<Usage>('GET', `/usage?period=${period}`),
}

export const LOGIN_URL = `${API_BASE}/auth/login`
